import React, { useState, useEffect } from 'react';
import { ArrowLeft, Edit, Trash2, Star, RefreshCw, AlertCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { Testimonial } from '../../services/admin/adminService';

const TestimonialDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [testimonial, setTestimonial] = useState<Testimonial | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTestimonial();
    }
  }, [id]);

  const fetchTestimonial = async () => {
    try {
      setLoading(true);
      const data = await adminService.getTestimonialById(Number(id));
      setTestimonial(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch testimonial');
      navigate('/admin/testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!testimonial) return;
    
    try {
      setDeleting(true);
      await adminService.deleteTestimonial(testimonial.id);
      toast.success('Testimonial deleted successfully!');
      navigate('/admin/testimonials');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete testimonial');
    } finally {
      setDeleting(false);
      setDeleteModal(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={24}
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
        <span className="ml-2 text-lg font-semibold text-secondary-900">({rating}/5)</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!testimonial) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Testimonial not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/testimonials')}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} className="text-secondary-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Testimonial Details</h1>
            <p className="text-secondary-600 mt-1">View and manage testimonial information</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/admin/testimonials/${testimonial.id}/edit`)}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Edit size={18} />
            Edit
          </button>
          <button
            onClick={() => setDeleteModal(true)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      {/* Details Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Name and Profile */}
          <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              {testimonial.profile ? (
                <img
                  src={testimonial.profile}
                  alt={testimonial.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-primary-600 font-bold text-2xl">
                  {testimonial.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-secondary-900">{testimonial.name}</h2>
              {testimonial.location && (
                <p className="text-secondary-600 mt-1">{testimonial.location}</p>
              )}
              <div className="mt-3">
                {renderStars(testimonial.rating)}
              </div>
            </div>
          </div>

          {/* Testimonial Text */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Testimonial
            </label>
            <p className="text-secondary-900 text-lg leading-relaxed bg-secondary-50 p-4 rounded-lg">
              {testimonial.text}
            </p>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Status
              </label>
              <span
                className={`inline-block px-3 py-1 text-sm rounded-full font-semibold ${
                  testimonial.status === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : testimonial.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {testimonial.status.charAt(0).toUpperCase() + testimonial.status.slice(1)}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Featured
              </label>
              <span
                className={`inline-block px-3 py-1 text-sm rounded-full font-semibold ${
                  Boolean(testimonial.featured)
                    ? 'bg-primary-100 text-primary-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {Boolean(testimonial.featured) ? 'Yes' : 'No'}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Verified
              </label>
              <span
                className={`inline-block px-3 py-1 text-sm rounded-full font-semibold ${
                  Boolean(testimonial.verified)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {Boolean(testimonial.verified) ? 'Yes' : 'No'}
              </span>
            </div>

            {testimonial.category && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Category
                </label>
                <p className="text-secondary-900">{testimonial.category}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Helpful Count
              </label>
              <p className="text-secondary-900">{testimonial.helpful}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Created At
              </label>
              <p className="text-secondary-900">
                {new Date(testimonial.created_at).toLocaleString()}
              </p>
            </div>

            {testimonial.updated_at && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Updated At
                </label>
                <p className="text-secondary-900">
                  {new Date(testimonial.updated_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Technical Info */}
          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-secondary-700 mb-3">Technical Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="block text-secondary-600 mb-1">IP Address</label>
                <p className="text-secondary-900 font-mono">{testimonial.ip_address}</p>
              </div>
              <div>
                <label className="block text-secondary-600 mb-1">User Agent</label>
                <p className="text-secondary-900 text-xs break-all">{testimonial.user_agent}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900">Delete Testimonial</h3>
              </div>
              
              <p className="text-secondary-600 mb-6">
                Are you sure you want to delete the testimonial from{' '}
                <strong>{testimonial.name}</strong>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialDetails;
